import React from "react"
import { 
  PremiumButton, 
  PremiumCard, 
  PremiumCardHeader, 
  PremiumCardTitle, 
  PremiumCardDescription, 
  PremiumCardContent,
  PremiumBadge,
  PremiumMetricsCard,
  PremiumConversationCard,
  PremiumNavigation,
  NavigationItem
} from "@/components/ui"
import { 
  RocketLaunchIcon, 
  ChatBubbleLeftRightIcon, 
  ChartBarIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  SparklesIcon
} from "@heroicons/react/24/outline"

const PremiumShowcase: React.FC = () => {
  const navigationItems: NavigationItem[] = [
    {
      id: "dashboard",
      label: "Dashboard",
      icon: <ChartBarIcon className="w-4 h-4" />,
      isActive: true
    },
    {
      id: "conversations",
      label: "Conversations",
      icon: <ChatBubbleLeftRightIcon className="w-4 h-4" />,
      badge: { text: "23", variant: "accent" }
    },
    {
      id: "campaigns",
      label: "Campaigns",
      icon: <RocketLaunchIcon className="w-4 h-4" />,
      badge: { text: "5", variant: "primary" }
    },
    {
      id: "customers",
      label: "Customers",
      icon: <UserGroupIcon className="w-4 h-4" />
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-primary/5 to-accent/5 p-8">
      <div className="max-w-7xl mx-auto space-y-12">
        {/* Header */}
        <div className="text-center space-y-4">
          <h1 className="text-4xl font-bold text-gradient">
            Bombard Premium UI System
          </h1>
          <p className="text-xl text-muted-foreground">
            Billion-dollar B2B SaaS aesthetics with Ultramarine Purple & Electric Cherry
          </p>
        </div>

        {/* Button Showcase */}
        <PremiumCard variant="elevated" glow="medium" className="p-8">
          <PremiumCardHeader>
            <PremiumCardTitle className="flex items-center gap-2">
              <SparklesIcon className="w-5 h-5 text-primary" />
              Premium Buttons
            </PremiumCardTitle>
            <PremiumCardDescription>
              Ultra-modern button variants with gradients and micro-interactions
            </PremiumCardDescription>
          </PremiumCardHeader>
          <PremiumCardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <PremiumButton variant="primary" size="lg">
                Primary Action
              </PremiumButton>
              <PremiumButton variant="accent" size="lg">
                Electric Cherry
              </PremiumButton>
              <PremiumButton variant="premium" size="lg">
                Premium
              </PremiumButton>
              <PremiumButton variant="glass" size="lg">
                Glass Morph
              </PremiumButton>
              <PremiumButton variant="success" glow="medium">
                Success
              </PremiumButton>
              <PremiumButton variant="warning" glow="medium">
                Warning
              </PremiumButton>
              <PremiumButton variant="destructive" glow="medium">
                Destructive
              </PremiumButton>
              <PremiumButton variant="outline" size="lg">
                Outline
              </PremiumButton>
            </div>
          </PremiumCardContent>
        </PremiumCard>

        {/* Navigation Showcase */}
        <PremiumCard variant="elevated" glow="subtle" className="p-8">
          <PremiumCardHeader>
            <PremiumCardTitle>Premium Navigation</PremiumCardTitle>
            <PremiumCardDescription>
              Sophisticated navigation with badges and smooth transitions
            </PremiumCardDescription>
          </PremiumCardHeader>
          <PremiumCardContent>
            <PremiumNavigation 
              items={navigationItems}
              variant="default"
              className="bg-secondary/30 rounded-lg p-4"
            />
          </PremiumCardContent>
        </PremiumCard>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <PremiumMetricsCard
            title="Total Campaigns"
            value="12"
            change="+23% this month"
            changeType="increase"
            icon={<RocketLaunchIcon className="w-5 h-5" />}
            badge={{ text: "Active", variant: "success" }}
            variant="gradient"
            trend="up"
          />
          <PremiumMetricsCard
            title="Response Rate"
            value="18.4%"
            change="+5.2% vs last month"
            changeType="increase"
            icon={<ChartBarIcon className="w-5 h-5" />}
            description="Above industry average"
            variant="success"
            trend="up"
          />
          <PremiumMetricsCard
            title="Total Contacts"
            value="2,847"
            change="+340 this week"
            changeType="increase"
            icon={<UserGroupIcon className="w-5 h-5" />}
            description="Across all campaigns"
            variant="accent"
            trend="up"
          />
          <PremiumMetricsCard
            title="Revenue Generated"
            value="$47,200"
            change="+28% this quarter"
            changeType="increase"
            icon={<CurrencyDollarIcon className="w-5 h-5" />}
            description="From closed deals"
            variant="gradient"
            trend="up"
          />
        </div>

        {/* Badge Showcase */}
        <PremiumCard variant="elevated" className="p-8">
          <PremiumCardHeader>
            <PremiumCardTitle>Premium Badges</PremiumCardTitle>
            <PremiumCardDescription>
              Status indicators and labels with sophisticated styling
            </PremiumCardDescription>
          </PremiumCardHeader>
          <PremiumCardContent>
            <div className="flex flex-wrap gap-3">
              <PremiumBadge variant="primary" dot>Active</PremiumBadge>
              <PremiumBadge variant="accent" dot>Priority</PremiumBadge>
              <PremiumBadge variant="success" dot>Completed</PremiumBadge>
              <PremiumBadge variant="warning" dot>Pending</PremiumBadge>
              <PremiumBadge variant="destructive" dot>Escalated</PremiumBadge>
              <PremiumBadge variant="ai-processing" dot>AI Processing</PremiumBadge>
              <PremiumBadge variant="premium" size="lg">Premium</PremiumBadge>
              <PremiumBadge variant="glass" size="lg">Glass Effect</PremiumBadge>
            </div>
          </PremiumCardContent>
        </PremiumCard>

        {/* Conversation Cards */}
        <PremiumCard variant="elevated" className="p-8">
          <PremiumCardHeader>
            <PremiumCardTitle>Premium Conversation Cards</PremiumCardTitle>
            <PremiumCardDescription>
              Sophisticated messaging interface components
            </PremiumCardDescription>
          </PremiumCardHeader>
          <PremiumCardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <PremiumConversationCard
                status="active"
                contact={{
                  name: "Sarah Johnson",
                  email: "sarah@techflow.com",
                  company: "TechFlow Solutions"
                }}
                avatar={{
                  fallback: "SJ",
                  status: "online"
                }}
                lastMessage={{
                  text: "Thanks for the detailed proposal. We'd like to schedule a call to discuss next steps.",
                  timestamp: "2 hours ago",
                  sender: "contact"
                }}
                stats={{
                  messageCount: 12,
                  unreadCount: 2
                }}
                tags={[
                  { text: "Hot Lead", variant: "accent" },
                  { text: "Enterprise", variant: "primary" }
                ]}
                campaign={{
                  id: "camp123",
                  name: "Q4 Enterprise Outreach"
                }}
              />
              <PremiumConversationCard
                status="ai-processing"
                contact={{
                  name: "Michael Chen",
                  email: "m.chen@innovate.co",
                  company: "Innovate Co"
                }}
                avatar={{
                  fallback: "MC",
                  status: "away"
                }}
                lastMessage={{
                  text: "AI is generating a personalized response based on the conversation context...",
                  timestamp: "Just now",
                  sender: "ai"
                }}
                stats={{
                  messageCount: 8
                }}
                tags={[
                  { text: "AI Assisted", variant: "primary" },
                  { text: "Follow-up", variant: "warning" }
                ]}
                campaign={{
                  id: "camp456",
                  name: "AI-Powered Sequences"
                }}
              />
            </div>
          </PremiumCardContent>
        </PremiumCard>

        {/* Card Variants */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <PremiumCard variant="glass" className="p-6">
            <h3 className="text-lg font-semibold mb-2">Glass Morphism</h3>
            <p className="text-muted-foreground">
              Beautiful frosted glass effect with backdrop blur
            </p>
          </PremiumCard>
          <PremiumCard variant="gradient" glow="strong" className="p-6">
            <h3 className="text-lg font-semibold mb-2">Gradient Border</h3>
            <p className="text-muted-foreground">
              Subtle gradient borders with premium glow
            </p>
          </PremiumCard>
          <PremiumCard variant="interactive" className="p-6">
            <h3 className="text-lg font-semibold mb-2">Interactive</h3>
            <p className="text-muted-foreground">
              Hover effects with smooth micro-interactions
            </p>
          </PremiumCard>
        </div>

        {/* Footer */}
        <div className="text-center py-8">
          <p className="text-muted-foreground">
            Built with Shadcn/ui • Tailwind CSS • React • TypeScript
          </p>
        </div>
      </div>
    </div>
  )
}

export default PremiumShowcase 